import { BackSide, Color, DoubleSide, FrontSide, LinearFilter, MeshBasicMaterial, NearestFilter, PointsMaterial, RepeatWrapping, ShaderMaterial, Texture, UVMapping, } from "three";
import { hasOwnProperty, isDefined } from "../internal";
/**
 * Handle styles and export them to suitable materials textures etc.
 * @class StyleUtils
 * @static
 * @author Robert Wüest
 * @constructor
 */
export class StyleUtils {
    /**
     * @method CreatePointMaterial
     * @param {Object} mat
     * @param {Engine} engine
     * @param {MaterialAnimation?} materialAnimation
     * @param {usefontcolor?} usefontcolor
     * @param symbolLoadSuccess
     * @return {Material}
     */
    static CreatePointMaterial(mat, engine, materialAnimation, usefontcolor, symbolLoadSuccess) {
        let material = null;
        let materialParams = {};
        if (hasOwnProperty(mat, "stroke")) {
            const stroke = mat["stroke"];
            const textureloaded = (texture) => {
                materialParams["map"] = texture;
                if (isDefined(symbolLoadSuccess) && symbolLoadSuccess != null)
                    symbolLoadSuccess(texture);
            };
            //Set a default marker for points without billboard or fontMarker Definition.
            if (!hasOwnProperty(stroke, "billboard") && !hasOwnProperty(stroke, "fontMarker")) {
                stroke["fontMarker"] = {
                    name: "circle",
                };
            }
            if (hasOwnProperty(stroke, "size"))
                stroke["size"] *= 2;
            if (hasOwnProperty(stroke, "billboard")) {
                materialParams["map"] = engine.requestSymbol("billboard", stroke["billboard"], textureloaded, null);
            }
            if (hasOwnProperty(stroke, "fontMarker")) {
                const fontMarker = stroke["fontMarker"];
                usefontcolor = !isDefined(usefontcolor) ? false : usefontcolor;
                if (hasOwnProperty(stroke, "color")) {
                    fontMarker["fillStyle"] = usefontcolor ? stroke["color"] : "rgba(255, 255, 255, 1.0)";
                }
                const fontmarkerdefinition = this.ParseFontMarkerParams(fontMarker, hasOwnProperty(stroke, "size") ? stroke["size"] : 20);
                materialParams["map"] = engine.requestSymbol("fontMarker", fontmarkerdefinition.fontkey, textureloaded, null, fontmarkerdefinition);
            }
            materialParams["depthTest"] = false;
            if (hasOwnProperty(stroke, "sizeAttenuation")) {
                materialParams["sizeAttenuation"] = stroke["sizeAttenuation"];
            }
            if (hasOwnProperty(stroke, "alphaTest")) {
                materialParams["alphaTest"] = stroke["alphaTest"];
            }
            if (hasOwnProperty(stroke, "color")) {
                materialParams["color"] = stroke["color"];
            }
            if (hasOwnProperty(stroke, "vertexColors") && stroke["vertexColors"] == true) {
                materialParams["vertexColors"] = true;
            }
            if (hasOwnProperty(stroke, "size")) {
                materialParams["size"] = stroke["size"];
            }
            if (hasOwnProperty(stroke, "alpha")) {
                materialParams["transparent"] = true;
                materialParams["opacity"] = stroke["alpha"];
            }
        }
        else {
            // Default values
            materialParams = {
                size: 1,
                sizeAttenuation: false,
                color: "#FF0000",
            };
        }
        if (material == null)
            material = new PointsMaterial(materialParams);
        return material;
    }
    static CreateLineMaterial(mat, engine, materialAnimation) {
        let stroke = {};
        const _vs = "\
        #ifdef GL_ES\n\
        precision highp float;\n\
        #endif\n\
        \n\
        uniform float linewidth;    // width of line\n\
        uniform float width;        // screen width\n\
        uniform float height;       // screen height\n\
        uniform float dashwidth;        // line dash width\n\
        uniform float gapwidth;       // line gap width\n\
        attribute vec3 color;           // vertex color\n\
        attribute vec3 start;           // start position of line\n\
        attribute vec3 end;           // end position of line\n\
        attribute vec3 linevertex;           // position of line\n\
        attribute float discarded;     // > 0.0 if a vertex is discarded else 0.0\n\
        varying vec3 vColor;\n\
        varying vec3 vColor2;\n\
        varying vec2 vUv;\n\
        varying float line_width;\n\
        varying float dash_width;\n\
        varying float gap_width;\n\
        varying vec3 vPosition;\n\
        varying float segmentLength;\n\
        varying float discardFragment;\n\
        float distancePlanePoint(vec4 plane, vec3 point)\n\
        {\n\
            float res = plane.x * point.x +\n\
                plane.y * point.y +\n\
                plane.z * point.z + plane.w;\n\
            return res;\n\
        }\n\
        vec4 getNearPlaneFromMVP(mat4 mvp){\n\
            float nearX = mvp[0][3] + mvp[0][2];\n\
            float nearY = mvp[1][3] + mvp[1][2];\n\
            float nearZ = mvp[2][3] + mvp[2][2];\n\
            float nearD = mvp[3][3] + mvp[3][2];\n\
            float mag = sqrt(nearX*nearX+nearY*nearY+nearZ*nearZ);\n\
            return vec4(nearX/mag,nearY/mag,nearZ/mag,nearD/mag);\n\
        }\n\
        vec4 getBottomPlaneFromMVP(mat4 mvp){\n\
            float bottomX = mvp[0][3] + mvp[0][1];\n\
            float bottomY = mvp[1][3] + mvp[1][1];\n\
            float bottomZ = mvp[2][3] + mvp[2][1];\n\
            float bottomD = mvp[3][3] + mvp[3][1];\n\
            float mag = sqrt(bottomX*bottomX+bottomY*bottomY+bottomZ*bottomZ);\n\
            return vec4(bottomX/mag,bottomY/mag,bottomZ/mag,bottomD/mag);\n\
        }\n\
        vec4 getLeftPlaneFromMVP(mat4 mvp){\n\
            float leftX = mvp[0][3] + mvp[0][0];\n\
            float leftY = mvp[1][3] + mvp[1][0];\n\
            float leftZ = mvp[2][3] + mvp[2][0];\n\
            float leftD = mvp[3][3] + mvp[3][0];\n\
            float mag = sqrt(leftX*leftX+leftY*leftY+leftZ*leftZ);\n\
            return vec4(leftX/mag,leftY/mag,leftZ/mag,leftD/mag);\n\
        }\n\
        vec4 getRightPlaneFromMVP(mat4 mvp){\n\
            float rightX = mvp[0][3] - mvp[0][0];\n\
            float rightY = mvp[1][3] - mvp[1][0];\n\
            float rightZ = mvp[2][3] - mvp[2][0];\n\
            float rightD = mvp[3][3] - mvp[3][0];\n\
            float mag = sqrt( rightX* rightX+ rightY* rightY+ rightZ* rightZ);\n\
            return vec4( rightX/mag, rightY/mag, rightZ/mag, rightD/mag);\n\
        }\n\
        vec4 getTopPlaneFromMVP(mat4 mvp){\n\
            float topX = mvp[0][3] - mvp[0][1];\n\
            float topY = mvp[1][3] - mvp[1][1];\n\
            float topZ = mvp[2][3] - mvp[2][1];\n\
            float topD = mvp[3][3] - mvp[3][1];\n\
            float mag = sqrt( topX* topX+ topY* topY+ topZ* topZ);\n\
            return vec4( topX/mag, topY/mag, topZ/mag, topD/mag);\n\
        }\n\
        vec4 getFarPlaneFromMVP(mat4 mvp){\n\
            float farX = mvp[0][3] - mvp[0][2];\n\
            float farY = mvp[1][3] - mvp[1][2];\n\
            float farZ = mvp[2][3] - mvp[2][2];\n\
            float farD = mvp[3][3] - mvp[3][2];\n\
            float mag = sqrt( farX* farX+ farY* farY+ farZ* farZ);\n\
            return vec4( farX/mag, farY/mag, farZ/mag, farD/mag);\n\
        }\n\
        float pointInFrustum(vec4 leftplane,vec4 topplane,vec4 rightplane,vec4 bottomplane,vec4 nearplane,vec4 farplane, vec3 point)\n\
        {\n\
            float d = distancePlanePoint(leftplane,point);\n\
            if(d<0.0) \n\
            {\n\
                return 0.0;\n\
            }\n\
            d = distancePlanePoint(topplane,point);\n\
            if(d<0.0) \n\
            {\n\
                return 0.0;\n\
            }\n\
            d = distancePlanePoint(rightplane,point);\n\
            if(d<0.0) \n\
            {\n\
                return 0.0;\n\
            }\n\
            d = distancePlanePoint(bottomplane,point);\n\
            if(d<0.0) \n\
            {\n\
                return 0.0;\n\
            }\n\
            d = distancePlanePoint(nearplane,point);\n\
            if(d<0.0) \n\
            {\n\
                return 0.0;\n\
            }\n\
            d = distancePlanePoint(farplane,point);\n\
            if(d<0.0) \n\
            {\n\
                return 0.0;\n\
            }\n\
            return 1.0;\n\
        }\n\
        vec3 linePlaneIntersection(vec4 plane, vec3 start, vec3 end)\n\
        {\n\
            vec3 direction = end-start;\n\
            float denominator = dot(vec3(plane.x,plane.y,plane.z),direction);\n\
            if(denominator == 0.0)\n\
            {\n\
                return vec3(0.0,0.0,0.0);\n\
            }\n\
            float t = - ( dot(start,vec3(plane.x,plane.y,plane.z) ) + plane.w-0.0001) / denominator;\n\
            if ( t < 0.0 || t > 1.0 ) {\n\
                return vec3(0.0,0.0,0.0);\n\
            }\n\
            return direction*t+start;\n\
        }\n\
        void main()\n\
        {\n\
            mat4 mvp = projectionMatrix * modelViewMatrix; // modelviewprojection matrix\n\
            vColor = color;\n\
            vPosition = linevertex;\n\
            discardFragment = 0.0;\n\
            vec4 leftplane = getLeftPlaneFromMVP(mvp);\n\
            vec4 topplane = getTopPlaneFromMVP(mvp);\n\
            vec4 rightplane = getRightPlaneFromMVP(mvp);\n\
            vec4 bottomplane = getBottomPlaneFromMVP(mvp);\n\
            vec4 nearplane = getNearPlaneFromMVP(mvp);\n\
            vec4 farplane = getFarPlaneFromMVP(mvp);\n\
            float startinside = pointInFrustum(leftplane,topplane,rightplane,bottomplane,nearplane,farplane,start);\n\
            float endinside = pointInFrustum(leftplane,topplane,rightplane,bottomplane,nearplane,farplane,end);\n\
            vec3 cstart = vec3(0,0,0);\n\
            vec3 cend = vec3(0,0,0);\n\
            if(startinside > 0.5 && endinside > 0.5)\n\
            {//both point are inside frustum no clipping\n\
                cstart = start;\n\
                cend = end;\n\
                vColor2 = vec3(1.0,1.0,1.0);\n\
            }\n\
            else //there is an intersection\n\
            {\n\
            if(startinside > 0.5){cstart = start;}\n\
            if(endinside > 0.5){cend = end;}\n\
            float startset = 0.0;\n\
            vec3 ip = linePlaneIntersection(leftplane,start,end);\n\
            if(ip.x!=0.0)\n\
            {\n//intersection with left plane, check if ip lies in frustum\n\
                if(pointInFrustum(leftplane,topplane,rightplane,bottomplane,nearplane,farplane,ip)>0.5) \n\
                {\n\
                    if(startinside>0.5) \n\
                    {\n\
                    cend = ip;\n\
                    }\n\
                    if(endinside>0.5)\n\
                    {\n\
                    cstart = ip;\n\
                    }\n\
                    if(endinside<0.5 && startinside<0.5)\n\
                    {\n\
                    if(startset <0.5) \n\
                    {\n\
                        cstart = ip;\n\
                        startset=1.0;\n\
                    }else {\n\
                        cend = ip;\n\
                    }\n\
                    }\n\
                }\n\
            }\n\
            ip = linePlaneIntersection(rightplane,start,end);\n\
            if(ip.x!=0.0)\n\
            {\n//intersection with left plane, check if ip lies in frustum\n\
                if(pointInFrustum(leftplane,topplane,rightplane,bottomplane,nearplane,farplane,ip)>0.5) \n\
                {\n\
                    if(startinside>0.5) \n\
                    {\n\
                    cend = ip;\n\
                    }\n\
                    if(endinside>0.5)\n\
                    {\n\
                    cstart = ip;\n\
                    }\n\
                    if(endinside<0.5 && startinside<0.5)\n\
                    {\n\
                    if(startset <0.5) \n\
                    {\n\
                        cstart = ip;\n\
                        startset=1.0;\n\
                    }else {\n\
                        cend = ip;\n\
                    }\n\
                    }\n\
                }\n\
            }\n\
            ip = linePlaneIntersection(topplane,start,end);\n\
            if(ip.x!=0.0)\n\
                {\n//intersection with left plane, check if ip lies in frustum\n\
                if(pointInFrustum(leftplane,topplane,rightplane,bottomplane,nearplane,farplane,ip)>0.5) \n\
                {\n\
                    if(startinside>0.5) \n\
                    {\n\
                    cend = ip;\n\
                    }\n\
                    if(endinside>0.5)\n\
                    {\n\
                    cstart = ip;\n\
                    }\n\
                    if(endinside<0.5 && startinside<0.5)\n\
                    {\n\
                    if(startset <0.5) \n\
                    {\n\
                        cstart = ip;\n\
                        startset=1.0;\n\
                    }else {\n\
                        cend = ip;\n\
                    }\n\
                    }\n\
                }\n\
            }\n\
            ip = linePlaneIntersection(bottomplane,start,end);\n\
            if(ip.x!=0.0)\n\
                {\n//intersection with left plane, check if ip lies in frustum\n\
                if(pointInFrustum(leftplane,topplane,rightplane,bottomplane,nearplane,farplane,ip)>0.5) \n\
                {\n\
                    if(startinside>0.5) \n\
                    {\n\
                    cend = ip;\n\
                    }\n\
                    if(endinside>0.5)\n\
                    {\n\
                    cstart = ip;\n\
                    }\n\
                    if(endinside<0.5 && startinside<0.5)\n\
                    {\n\
                    if(startset <0.5) \n\
                    {\n\
                        cstart = ip;\n\
                        startset=1.0;\n\
                    }else {\n\
                        cend = ip;\n\
                    }\n\
                    }\n\
                }\n\
            }\n\
            ip = linePlaneIntersection(nearplane,start,end);\n\
            if(ip.x!=0.0)\n\
            {\n//intersection with left plane, check if ip lies in frustum\n\
                if(pointInFrustum(leftplane,topplane,rightplane,bottomplane,nearplane,farplane,ip)>0.5) \n\
                {\n\
                    vColor2 = vec3(1.0,1.0,0.0);\n\
                    if(startinside>0.5) \n\
                    {\n\
                    cend = ip;\n\
                    }\n\
                    if(endinside>0.5)\n\
                    {\n\
                    cstart = ip;\n\
                    }\n\
                    if(endinside<0.5 && startinside<0.5)\n\
                    {\n\
                    if(startset <0.5) \n\
                    {\n\
                        cstart = ip;\n\
                        startset=1.0;\n\
                    }else {\n\
                        cend = ip;\n\
                    }\n\
                    }\n\
                }\n\
            }\n\
            ip = linePlaneIntersection(farplane,start,end);\n\
            if(ip.x!=0.0)\n\
            {\n//intersection with left plane, check if ip lies in frustum\n\
                if(pointInFrustum(leftplane,topplane,rightplane,bottomplane,nearplane,farplane,ip)>0.5) \n\
                {\n\
                    if(startinside>0.5) \n\
                    {\n\
                    cend = ip;\n\
                    }\n\
                    if(endinside>0.5)\n\
                    {\n\
                    cstart = ip;\n\
                    }\n\
                    if(endinside<0.5 && startinside<0.5)\n\
                    {\n\
                    if(startset <0.5) \n\
                    {\n\
                        cstart = ip;\n\
                        startset=1.0;\n\
                    }else {\n\
                        cend = ip;\n\
                    }\n\
                    }\n\
                }\n\
            }\n\
            }\n\
                if(cstart.x==0.0 || cend.x==0.0 )\n\
            {\n\
                discardFragment = 1.0;\n\
            }\n\
            vec4 startT = mvp * vec4(cstart,1.0);\n\
            startT = startT/startT.w;\n\
            vec4 endT = mvp * vec4(cend,1.0);\n\
            endT = endT/endT.w;\n\
            float dx = endT.x-startT.x;\n\
            float dy = endT.y-startT.y;\n\
            float len= sqrt(dx*dx + dy*dy);\n\
            segmentLength = len;\n\
            float alpha = atan(dy,dx);\n\
            vec3 pos;\n\
            pos.x = startT.x + linevertex.x*len * cos(alpha) - linevertex.y/width*linewidth * sin(alpha);//startT.x + (0.02*linevertex.x);//\n\
            pos.y = startT.y + linevertex.y/height*linewidth * cos(alpha) + linevertex.x*len * sin(alpha);//endT.y + (0.01*linevertex.y);//startT.y + (0.02*linevertex.y);//\n\
            pos.z = 1.0;//startT.z;-> Achtung sonst mikado-bug!\n\
            //vec4 pos = startT+vec4(linevertex.x*len * cos(alpha) - linevertex.y/width*linewidth * sin(alpha),linevertex.y/height*linewidth * cos(alpha) + linevertex.x*len * sin(alpha),+0.2, 1.0);\n\
            gl_Position = vec4(pos,1.0);\n\
            float ddx = endT.x*width-startT.x*width;\n\
            float ddy = endT.y*height-startT.y*height;\n\
            float lenP = sqrt(ddx*ddx + ddy*ddy)/((dashwidth+gapwidth)*2.0);\n\
            vUv = uv;\n\
            vUv.x = uv.x*lenP;\n\
            line_width = linewidth;\n\
            dash_width = dashwidth;\n\
            gap_width = gapwidth;\n\
        }\n";
        let _fs = "\
        #ifdef GL_ES\n\
        precision highp float;\n\
        #endif\n\
        \n\
        uniform float opacity;\n\
        uniform vec3 linecolor;  // sRGB color from JS\n\
        uniform sampler2D tex;\n\
        uniform float time;\n\
        varying vec3 vColor2;\n\
        varying vec3 vColor;\n\
        varying vec3 vPosition;\n\
        varying float segmentLength;\n\
        varying vec2 vUv;\n\
        varying float discardFragment;\n\
        \n\
        void main()\n\
        {\n\
            //float timeFactor = mod(time,1.0);\n\
            vec4 color = vec4(linecolor, opacity);\n\
            vec2 uvw = vUv;\n\
            if( discardFragment > 0.5)\n\
            {\n\
                discard;\n\
            }\n\
            //{animatedDashes}\n\
            //{sinusColor}\n\
            //{sinusMix}\n\
            //{sinusColorEnabled}color = mix(color, sinusColor, sinusMix);\n\
            vec4 map = texture2D(tex, uvw);\n\
            map.rgb = color.rgb;\n\
            //gl_FragColor = vec4(vColor2, 1.0);\n\
            gl_FragColor = map;\n\
        }\n\
        ";
        if (hasOwnProperty(mat, "stroke"))
            stroke = mat["stroke"];
        let color = "#FF0000";
        let gapWidth = 5;
        if (hasOwnProperty(stroke, "gapWidth"))
            gapWidth = stroke["gapWidth"];
        let dashWidth = 0;
        if (hasOwnProperty(stroke, "dashWidth")) {
            dashWidth = stroke["dashWidth"];
        }
        else {
            _fs = _fs.replace("gl_FragColor = map;", "gl_FragColor = color;");
        }
        if (hasOwnProperty(stroke, "color"))
            color = stroke["color"];
        let lineWidth = 1;
        if (hasOwnProperty(stroke, "width"))
            lineWidth = stroke["width"];
        let alpha = 1.0;
        if (hasOwnProperty(stroke, "alpha"))
            alpha = stroke["alpha"];
        let round_Cap = false;
        if (hasOwnProperty(stroke, "roundCap"))
            round_Cap = stroke["roundCap"];
        if (hasOwnProperty(stroke, "animatedDashes")) {
            if (stroke["animatedDashes"]) {
                _fs = _fs.replace("//{animatedDashes}", "uvw.x += time;");
            }
        }
        if (hasOwnProperty(stroke, "sinusColor")) {
            if (stroke["sinusColor"]) {
                const sinColor = new Color(stroke["sinusColor"].toString());
                _fs = _fs.replace("//{sinusMix}", "float sinusMix = 0.5+sin( vPosition.x*segmentLength*50.0 + time*3.0)*0.5;");
                _fs = _fs.replace("//{sinusColorEnabled}", "");
                const sinusAlpha = hasOwnProperty(stroke, "sinusColorAlpha")
                    ? stroke["sinusColorAlpha"].toString()
                    : "1.0";
                _fs = _fs.replace("//{sinusColor}", "vec4 sinusColor = vec4(" +
                    sinColor.r.toString() +
                    "," +
                    sinColor.g.toString() +
                    "," +
                    sinColor.b.toString() +
                    "," +
                    sinusAlpha +
                    ");");
            }
        }
        if (hasOwnProperty(stroke, "flashColor") && !hasOwnProperty(stroke, "sinusColor")) {
            if (stroke["flashColor"]) {
                const flashColor = new Color(stroke["flashColor"].toString());
                _fs = _fs.replace("//{sinusMix}", "float sinusMix = 0.5+sin(time*4.0)*0.5;");
                _fs = _fs.replace("//{sinusColorEnabled}", "");
                const flashAlpha = hasOwnProperty(stroke, "flashColorAlpha")
                    ? stroke["flashColorAlpha"].toString()
                    : "1.0";
                _fs = _fs.replace("//{sinusColor}", "vec4 sinusColor = vec4(" +
                    flashColor.r.toString() +
                    "," +
                    flashColor.g.toString() +
                    "," +
                    flashColor.b.toString() +
                    "," +
                    flashAlpha +
                    ");");
            }
        }
        if (hasOwnProperty(stroke, "vertexColors") && stroke["vertexColors"] == true) {
            _fs = _fs.replace("vec4 color = vec4(linecolor, opacity);", "vec4 color = vec4(vColor, opacity);");
        }
        const createTexture = function (dashSize, gapSize, lineWidth, roundCap) {
            const nextPowerOf2 = function (v) {
                v--;
                v |= v >> 1;
                v |= v >> 2;
                v |= v >> 4;
                v |= v >> 8;
                v |= v >> 16;
                v++;
                return v;
            };
            const sizeX = nextPowerOf2(dashSize + gapSize);
            const scaleFactor = sizeX / (dashSize + gapSize);
            const sizeY = nextPowerOf2(lineWidth);
            // create canvas
            const canvas = document.createElement("canvas");
            canvas.width = sizeX;
            canvas.height = sizeY;
            // get context
            const context = canvas.getContext("2d");
            if (!roundCap) {
                // Rect
                context.beginPath();
                context.rect(0, 0, dashSize * scaleFactor, sizeY);
                context.fillStyle = "#FF00FF";
                context.fill();
            }
            else {
                // Round
                const radius = Math.floor(sizeY / 2.0);
                context.fillStyle = "#FF00FF";
                context.beginPath();
                context.rect(radius, 0, dashSize * scaleFactor - 2 * radius, sizeY);
                context.fill();
                context.beginPath();
                context.arc(radius, radius, radius, 0, 2 * Math.PI);
                context.fill();
                context.beginPath();
                context.arc(dashSize * scaleFactor - radius, radius, radius, 0, 2 * Math.PI);
                context.fill();
            }
            return canvas;
        };
        const tex = new Texture(createTexture(dashWidth, gapWidth, lineWidth, round_Cap));
        tex.wrapS = RepeatWrapping;
        tex.wrapT = RepeatWrapping;
        tex.generateMipmaps = false;
        tex.magFilter = LinearFilter;
        tex.minFilter = LinearFilter;
        tex.needsUpdate = true;
        const material = new ShaderMaterial({
            uniforms: {
                opacity: { value: alpha },
                linecolor: { value: new Color(color).convertLinearToSRGB() },
                linewidth: { value: lineWidth },
                dashwidth: { value: dashWidth },
                gapwidth: { value: gapWidth },
                width: { value: engine.getContextWidth() },
                height: { value: engine.getContextHeight() },
                time: { value: 0.0 },
                tex: { value: tex },
            },
            transparent: true,
            side: DoubleSide,
            depthTest: false,
            vertexShader: _vs,
            fragmentShader: _fs,
        });
        if (materialAnimation) {
            materialAnimation.callback = (deltaTime) => {
                material.uniforms.time.value += deltaTime;
            };
        }
        return material;
    }
    static CreatePolygonMaterial(mat, engine, materialAnimation) {
        let material = null;
        let materialParams = {};
        if (hasOwnProperty(mat, "fill")) {
            const fill = mat["fill"];
            if (!hasOwnProperty(fill, "side")) {
                fill["side"] = DoubleSide;
            }
            if (hasOwnProperty(fill, "color")) {
                materialParams["color"] = fill["color"];
            }
            if (hasOwnProperty(fill, "vertexColors") && fill["vertexColors"] == true) {
                materialParams["vertexColors"] = true;
            }
            if (hasOwnProperty(fill, "side")) {
                materialParams["side"] =
                    fill["side"] == DoubleSide ? DoubleSide : fill["side"] == BackSide ? BackSide : FrontSide;
            }
            if (hasOwnProperty(fill, "alpha")) {
                materialParams["transparent"] = true;
                materialParams["opacity"] = fill["alpha"];
            }
        }
        else {
            materialParams = {
                color: "#FF0000",
            };
        }
        materialParams["depthWrite"] = false;
        materialParams["depthTest"] = false;
        if (material == null)
            material = new MeshBasicMaterial(materialParams);
        return material;
    }
    static ParseFontMarkerParams(parameters, size) {
        const fmdefinition = {};
        if (parameters === undefined)
            parameters = {};
        fmdefinition.content = hasOwnProperty(parameters, "content") ? parameters["content"] : "A";
        fmdefinition.fontface = hasOwnProperty(parameters, "fontface") ? parameters["fontface"] : "Courier New";
        fmdefinition.fontsize = hasOwnProperty(parameters, "fontsize") ? parameters["fontsize"] : size;
        fmdefinition.rotation = hasOwnProperty(parameters, "rotation") ? parameters["rotation"] : 0;
        fmdefinition.anchor = hasOwnProperty(parameters, "anchor") ? parameters["anchor"] : ["center", "middle"];
        if (hasOwnProperty(parameters, "name") &&
            !hasOwnProperty(parameters, "content") &&
            !hasOwnProperty(parameters, "fontface")) {
            fmdefinition.fontface = "infra3d";
            switch (parameters["name"]) {
                case "square-o":
                    fmdefinition.content = "\ue90b";
                    break;
                case "square":
                    fmdefinition.content = "\ue90c";
                    break;
                case "location-arrow":
                    fmdefinition.content = "\ue909";
                    break;
                case "star-o":
                    fmdefinition.content = "\ue901";
                    break;
                case "star":
                    fmdefinition.content = "\ue902";
                    break;
                case "map-pin":
                    fmdefinition.content = "\ue903";
                    break;
                case "map-marker":
                    fmdefinition.content = "\ue904";
                    break;
                case "crosshairs":
                    fmdefinition.content = "\ue905";
                    break;
                case "circle-o":
                    fmdefinition.content = "\ue907";
                    break;
                case "circle":
                    fmdefinition.content = "\ue908";
                    break;
                case "cross":
                    fmdefinition.content = "\uea0f";
                    break;
            }
        }
        fmdefinition.fillStyle = hasOwnProperty(parameters, "fillStyle")
            ? parameters["fillStyle"]
            : "rgba(255, 255, 255, 1.0)";
        fmdefinition.fontkey =
            "ttf://" +
                fmdefinition.fontface +
                "#" +
                fmdefinition.content +
                "?fontsize=" +
                fmdefinition.fontsize.toString() +
                "&rotation=" +
                fmdefinition.rotation.toString() +
                "&anchor=" +
                fmdefinition.anchor.toString() +
                "&fillStyle=" +
                fmdefinition.fillStyle;
        return fmdefinition;
    }
    /**
     * @param {FontMarkerDefinition} fontmarkerdefs
     * @param infra3dcanvas
     * @param {(texture: Texture) => void} textureLoadSuccess
     * @param {(texture: Texture) => void} textureLoadFailed
     * @returns {Texture}
     * @constructor
     */
    static MakeTextTexture(fontmarkerdefs, textureLoadSuccess, textureLoadFailed) {
        const canvas = document.createElement("canvas");
        canvas.width = fontmarkerdefs.fontsize * 2;
        canvas.height = fontmarkerdefs.fontsize * 2;
        // canvas contents will be used for a texture
        const texture = new Texture(canvas);
        const fontloaded = () => {
            const context = canvas.getContext("2d");
            if (!isDefined(fontmarkerdefs.anchor)) {
                fontmarkerdefs.anchor = ["center", "middle"];
            }
            switch (fontmarkerdefs.anchor[1]) {
                case "middle":
                    context.textBaseline = "middle";
                    break;
                case "top":
                    context.textBaseline = "bottom";
                    break;
                case "bottom":
                    context.textBaseline = "top";
                    break;
            }
            switch (fontmarkerdefs.anchor[0]) {
                case "center":
                    context.textAlign = "center";
                    break;
                case "left":
                    context.textAlign = "right";
                    break;
                case "right":
                    context.textAlign = "left";
                    break;
            }
            context.translate(fontmarkerdefs.fontsize, fontmarkerdefs.fontsize);
            context.rotate(fontmarkerdefs.rotation * (Math.PI / 180.0));
            context.font = fontmarkerdefs.fontsize + "px " + fontmarkerdefs.fontface;
            context.fillStyle = fontmarkerdefs.fillStyle;
            context.fillText(fontmarkerdefs.content, 0, 0);
            texture.needsUpdate = true;
            texture.minFilter = LinearFilter;
            textureLoadSuccess(texture);
        };
        const fontloadedfailed = () => {
            console.log("Error: requested font " + fontmarkerdefs.fontface + " could not be loaded!");
            textureLoadFailed(texture);
        };
        let chars = fontmarkerdefs.content;
        if (fontmarkerdefs.fontface == "infra3d")
            chars = "\ue908";
        this.WaitForWebFonts([fontmarkerdefs.fontface], chars, fontloaded, fontloadedfailed);
        texture.needsUpdate = true;
        texture.minFilter = LinearFilter;
        return texture;
    }
    static WaitForWebFonts(fonts, chars, callback, errorcbf) {
        let loadedFonts = 0;
        for (let i = 0, l = fonts.length; i < l; ++i) {
            (function (font, chars) {
                let node = document.createElement("span");
                // Characters that vary significantly among different fonts
                node.innerHTML = chars; //'\ue908giItT1WQy@!-/#';//chars;//"\ue908";//chars;  //
                // 'giItT1WQy@!-/#';
                // Visible - so we can measure it - but not on the screen
                node.style.position = "absolute";
                node.style.left = "-1000px";
                node.style.top = "-1000px";
                // Large font size makes even subtle changes obvious
                node.style.fontSize = "300px";
                // Reset any font properties
                node.style.fontFamily = "sans-serif";
                node.style.fontVariant = "normal";
                node.style.fontStyle = "normal";
                node.style.fontWeight = "normal";
                node.style.letterSpacing = "0";
                document.body.appendChild(node);
                // Remember width with no applied web font
                const width = node.offsetWidth;
                node.style.fontFamily = font + ", sans-serif";
                let interval;
                let it = 0;
                const maxit = 5 * fonts.length;
                function checkFont() {
                    // Compare current width with original width
                    if (node && node.offsetWidth != width) {
                        ++loadedFonts;
                        node.parentNode.removeChild(node);
                        node = null;
                    }
                    // If all fonts have been loaded
                    if (loadedFonts >= fonts.length) {
                        if (interval)
                            clearInterval(interval);
                        if (loadedFonts == fonts.length) {
                            callback();
                            it = 0;
                            return true;
                        }
                    }
                    if (it >= maxit) {
                        if (interval)
                            clearInterval(interval);
                        node.parentNode.removeChild(node);
                        node = null;
                        errorcbf();
                        return false;
                    }
                    ++it;
                }
                if (!checkFont())
                    interval = setInterval(checkFont, 50);
            })(fonts[i], chars);
        }
    }
    /**
     * makes a square billboard-texture from an image-url. The image must not be square. The texture uses nearest image interpolation.
     * @param {string} key. The image url
     * @param {(texture: Texture) => void} textureLoadSuccess
     * @param {(texture: Texture) => void} textureLoadFailed
     * @returns {Texture}
     * @constructor
     */
    static MakeBillboardTexture(key, textureLoadSuccess, textureLoadFailed) {
        const canvas = document.createElement("canvas");
        const texture = new Texture(canvas, UVMapping);
        const image = new Image();
        image.onload = (event) => {
            const canvas_dim = Math.max(image.width, image.height);
            canvas.width = canvas_dim;
            canvas.height = canvas_dim;
            const context = canvas.getContext("2d");
            const offset_x = (canvas_dim - image.width) / 2;
            const offset_y = (canvas_dim - image.height) / 2;
            context.drawImage(image, offset_x, offset_y, image.width, image.height);
            texture.needsUpdate = true;
            textureLoadSuccess(texture);
        };
        image.onerror = (event) => {
            textureLoadFailed(texture);
        };
        image.crossOrigin = "anonymous";
        image.src = key;
        texture.magFilter = NearestFilter;
        texture.minFilter = NearestFilter;
        return texture;
    }
}
//# sourceMappingURL=styleutils.js.map